import * as Yup from "yup";
import {
  ChangeAdvokatselskabKind,
  ChangeInteressentskabKind,
  InterestPartyChangeDirection,
} from "../model/registrationEnums";
import type { InterestPartyChangeDirection as InterestPartyChangeDirectionType } from "../model/registrationEnums";
import {
  isEntersAdvokatselskabPartner,
  isEntersInterestParty,
  isLeavesAdvokatselskabPartner,
  isLeavesInterestParty,
} from "../change/changePartyEnterRules";
import {
  cvrField,
  pNumberField,
  remarksAndContactFields,
  requiredDate,
  SELECT_REQUIRED_MESSAGE,
} from "./registrationValidationShared";
import {
  changeAdvokatselskabEnteringPartyEligibilityFields,
  changeEnteringPartyEligibilityFields,
  changeLeavingPartyFields,
} from "../eligibility/participantEligibilityValidation";

const basePartyRow = {
  name: Yup.string().required("Navn er påkrævet."),
  cvrCpr: Yup.string().required("CVR/CPR er påkrævet."),
};

/**
 * Row-level tests need the form's `changeKind`, but that field lives on the
 * parent change object, not on each interestParties/partners row.
 *
 * During a row `.test()`, Yup exposes ancestors in `context.from`. For
 * `changeInteressentskab.interestParties[0]`, the chain is roughly:
 *   [0] = the row   { name, cvrCpr, direction, … }
 *   [1] = the array interestParties
 *   [2] = the root  { name, cvr, changeKind, interestParties, … }
 *
 * The last entry is the root object — that is where we read `changeKind` from,
 * so we can tell if a row is entering (and should run eligibility validation)
 * or leaving (name/CVR only).
 */
const getChangeKind = (context: Yup.TestContext): string => {
  const root = context.from?.[context.from.length - 1]?.value as
    { changeKind?: string } | undefined;

  return root?.changeKind ?? "";
};

const validateEntersParty = (
  context: Yup.TestContext,
  party: Record<string, unknown>,
  enteringEligibilityFields: Record<string, Yup.AnySchema>,
): boolean | Yup.ValidationError => {
  try {
    Yup.object(enteringEligibilityFields).validateSync(party, {
      abortEarly: false,
    });
    return true;
  } catch (error) {
    if (error instanceof Yup.ValidationError && error.inner[0]) {
      return context.createError({
        path: `${context.path}.${error.inner[0].path}`,
        message: error.inner[0].message,
      });
    }

    return false;
  }
};

const validateLeavesParty = (
  context: Yup.TestContext,
  party: Record<string, unknown>,
  leavingFields: Record<string, Yup.AnySchema>,
): boolean | Yup.ValidationError => {
  try {
    Yup.object(leavingFields).validateSync(party, {
      abortEarly: false,
    });
    return true;
  } catch (error) {
    if (error instanceof Yup.ValidationError && error.inner[0]) {
      return context.createError({
        path: `${context.path}.${error.inner[0].path}`,
        message: error.inner[0].message,
      });
    }

    return false;
  }
};

const createChangePartyRowSchema = (
  testName: string,
  isEnters: (
    changeKind: string,
    direction: InterestPartyChangeDirectionType,
  ) => boolean,
  isLeaves: (
    changeKind: string,
    direction: InterestPartyChangeDirectionType,
  ) => boolean,
  directionRequired: boolean,
  enteringEligibilityFields: Record<string, Yup.AnySchema>,
) =>
  Yup.object({
    ...basePartyRow,
    direction: directionRequired
      ? Yup.string()
          .required(SELECT_REQUIRED_MESSAGE)
          .oneOf(
            [
              InterestPartyChangeDirection.Enters,
              InterestPartyChangeDirection.Leaves,
            ],
            SELECT_REQUIRED_MESSAGE,
          )
      : Yup.string().notRequired(),
  }).test(testName, function validateChangeParty(party) {
    const changeKind = getChangeKind(this);
    const direction = String(
      party.direction ?? "",
    ) as InterestPartyChangeDirectionType;

    if (isEnters(changeKind, direction)) {
      return validateEntersParty(this, party, enteringEligibilityFields);
    }

    if (isLeaves(changeKind, direction)) {
      return validateLeavesParty(this, party, changeLeavingPartyFields);
    }

    return true;
  });

const changeInterestPartyRow = createChangePartyRowSchema(
  "interest-party-change",
  isEntersInterestParty,
  isLeavesInterestParty,
  false,
  changeEnteringPartyEligibilityFields,
);

const changeInterestPartyRowWithDirection = createChangePartyRowSchema(
  "interest-party-change",
  isEntersInterestParty,
  isLeavesInterestParty,
  true,
  changeEnteringPartyEligibilityFields,
);

const changeAdvokatselskabPartnerRow = createChangePartyRowSchema(
  "advokatselskab-partner-change",
  isEntersAdvokatselskabPartner,
  isLeavesAdvokatselskabPartner,
  false,
  changeAdvokatselskabEnteringPartyEligibilityFields,
);

const changeAdvokatselskabPartnerRowWithDirection = createChangePartyRowSchema(
  "advokatselskab-partner-change",
  isEntersAdvokatselskabPartner,
  isLeavesAdvokatselskabPartner,
  true,
  changeAdvokatselskabEnteringPartyEligibilityFields,
);

export const changeInteressentskabSchema = Yup.object({
  name: Yup.string().required("Navn på virksomhed er påkrævet."),
  cvr: cvrField,
  pNumber: pNumberField,
  changeKind: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .oneOf(
      [
        ChangeInteressentskabKind.InteressentInd,
        ChangeInteressentskabKind.InteressentUd,
        ChangeInteressentskabKind.InteressentBegge,
      ],
      SELECT_REQUIRED_MESSAGE,
    ),
  interestParties: Yup.array().when("changeKind", {
    is: ChangeInteressentskabKind.InteressentBegge,
    then: () =>
      Yup.array()
        .of(changeInterestPartyRowWithDirection)
        .min(1, "Angiv mindst én interessent."),
    otherwise: () =>
      Yup.array()
        .of(changeInterestPartyRow)
        .min(1, "Angiv mindst én interessent."),
  }),
  changeDate: requiredDate,
  ...remarksAndContactFields,
});

export const changeAdvokatselskabSchema = Yup.object({
  name: Yup.string().required("Navn på virksomhed er påkrævet."),
  cvr: cvrField,
  pNumber: pNumberField,
  changeKind: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .oneOf(
      [
        ChangeAdvokatselskabKind.PartnerInd,
        ChangeAdvokatselskabKind.PartnerUd,
        ChangeAdvokatselskabKind.PartnerBegge,
      ],
      SELECT_REQUIRED_MESSAGE,
    ),
  partners: Yup.array().when("changeKind", {
    is: ChangeAdvokatselskabKind.PartnerBegge,
    then: () =>
      Yup.array()
        .of(changeAdvokatselskabPartnerRowWithDirection)
        .min(1, "Angiv mindst én selskabsdeltager."),
    otherwise: () =>
      Yup.array()
        .of(changeAdvokatselskabPartnerRow)
        .min(1, "Angiv mindst én selskabsdeltager."),
  }),
  changeDate: requiredDate,
  ...remarksAndContactFields,
});
